import { BaseDatasetDropzone } from "../BaseDatasetDropzone/BaseDatasetDropzone";
import styles from "./AppMenu.module.css";
import HeaderActionIcon from "./HeaderActionIcon";
import { HEADER_HEIGHT } from "./constants";
import posthogEvents from "@app/config/posthog/posthog-events";
import { PERMISSIONS } from "@app/constants/permissions";
import { usePermissions } from "@app/hooks/usePermissions";
import { useCreateExperimentDraft } from "@app/queries/experiment";
import { useUserStore } from "@app/state/store";
import type { IconProps } from "@lib/common/components/Icon/Icon";
import Icon from "@lib/common/components/Icon/Icon";
import ModalContent from "@lib/common/components/modals/ModalContent";
import ModalHeader from "@lib/common/components/modals/ModalHeader";
import formatUserName from "@lib/common/utils/formatUserName";
import DatasetIcon from "@lib/dataset/DatasetIcon/DatasetIcon";
import ExperimentIcon from "@lib/experiment/ExperimentIcon/ExperimentIcon";
import Settings from "@lib/settings/Settings";
import type { MantineColorScheme } from "@mantine/core";
import { Badge, Flex, Menu, Modal, Text, VisuallyHidden, useMantineColorScheme } from "@mantine/core";
import { useDisclosure, useOs } from "@mantine/hooks";
import { spotlight } from "@mantine/spotlight";
import { usePostHog } from "posthog-js/react";
import { useCallback, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";

function MenuIcon({ name, color = "dimmed", size, ...props }: IconProps) {
  return <Icon name={name} color={color} size={size} {...props} fixedWidth />;
}

export default function AppMenu() {
  const user = useUserStore();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const dropzoneRef = useRef<() => void>(null);
  const createExperimentDraft = useCreateExperimentDraft();
  const [settingsModalOpened, settingsModalControl] = useDisclosure();
  const os = useOs();
  const posthog = usePostHog();
  const [opened, setIsOpen] = useState(false);
  const { setColorScheme, colorScheme } = useMantineColorScheme();

  const createAndNavigateToDraft = useCallback(async () => {
    posthog.capture(posthogEvents.app_menu.configure_new_experiment_menu_item_click);
    const draft = await createExperimentDraft.mutateAsync({ body: { name: "" } });
    await navigate(`/experiments/drafts/${draft.id}`);
  }, [navigate, createExperimentDraft, posthog]);

  const changeColorScheme = useCallback(
    (theme: MantineColorScheme) => {
      posthog.capture(posthogEvents.settings.theme_change, { theme });
      setColorScheme(theme);
    },
    [setColorScheme, posthog]
  );

  return (
    <>
      <Menu
        trigger="click-hover"
        classNames={{ itemSection: styles.itemSection }}
        styles={{ itemLabel: { marginLeft: 3 } }}
        arrowOffset={12}
        offset={{ alignmentAxis: 5 }}
        transitionProps={{ transition: "pop-top-left" }}
        shadow="xl"
        closeDelay={500}
        onChange={open => setIsOpen(open)}
      >
        <Menu.Target>
          <Flex
            h={HEADER_HEIGHT}
            w={HEADER_HEIGHT}
            align="center"
            justify="center"
            pos="fixed"
            top={0}
            left={0}
            pl={5}
            style={{ zIndex: "calc(var(--mantine-z-index-app) + 1)", cursor: "pointer" }}
          >
            <HeaderActionIcon aria-expanded={opened ? true : undefined}>
              <Icon name="bars" aria-label="Toggle menu" size="lg" />
            </HeaderActionIcon>
          </Flex>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            component={Link}
            to="/"
            td="none"
            leftSection={<MenuIcon name="home" />}
            onClick={() => posthog.capture(posthogEvents.app_menu.home_menu_item_click)}
          >
            Home
          </Menu.Item>
          <Menu.Item
            td="none"
            onClick={() => {
              posthog.capture(posthogEvents.app_menu.search_menu_item_click);
              spotlight.open();
            }}
            leftSection={<MenuIcon name="search" />}
            rightSection={
              <Text c="dimmed" size="xs">
                {os === "macos" ? "Cmd+K" : "Ctrl-K"}
              </Text>
            }
          >
            Search
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/experiments"
            td="none"
            leftSection={<ExperimentIcon size="xs" />}
            onClick={() => posthog.capture(posthogEvents.app_menu.experiments_menu_item_click)}
          >
            Experiments
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/datasets"
            td="none"
            leftSection={<DatasetIcon size="xs" />}
            onClick={() => posthog.capture(posthogEvents.app_menu.datasets_menu_item_click)}
          >
            Datasets
          </Menu.Item>
          <Menu.Item
            leftSection={<MenuIcon name="plus" variant="solid" size="0.7rem" />}
            td="none"
            onClick={createAndNavigateToDraft}
          >
            New experiment
          </Menu.Item>
          <Menu.Item
            leftSection={<MenuIcon name="upload" size="0.7rem" />}
            td="none"
            onClick={() => {
              posthog.capture(posthogEvents.app_menu.upload_dataset_menu_item_click);
              dropzoneRef.current?.();
            }}
          >
            Upload dataset
          </Menu.Item>
          <Menu.Divider />
          {user && (
            <Menu.Item
              component={Link}
              to="/profile"
              td="none"
              leftSection={
                <Badge color="green" variant="light" size="sm" styles={{ root: { fontSize: "0.7rem" } }}>
                  {formatUserName(user).charAt(0)}
                </Badge>
              }
              onClick={() => posthog.capture(posthogEvents.app_menu.profile_menu_item_click)}
            >
              {formatUserName(user)}
            </Menu.Item>
          )}
          {hasPermission(PERMISSIONS.HAS_ACCESS_TO_ORG_ADMIN) && (
            <Menu.Item
              component={Link}
              to="/admin"
              leftSection={<MenuIcon name="buildings" />}
              td="none"
              onClick={() => posthog.capture(posthogEvents.app_menu.organization_menu_item_click)}
            >
              Organization
            </Menu.Item>
          )}

          <Menu.Sub openDelay={120} closeDelay={150}>
            <Menu.Sub.Target>
              <Menu.Sub.Item leftSection={<MenuIcon name="circle-half-stroke" />}>Theme</Menu.Sub.Item>
            </Menu.Sub.Target>
            <Menu.Sub.Dropdown>
              <Menu.Item
                rightSection={colorScheme === "light" && <Icon name="check" />}
                onClick={() => changeColorScheme("light")}
              >
                Light
              </Menu.Item>
              <Menu.Item
                rightSection={colorScheme === "dark" && <Icon name="check" />}
                onClick={() => changeColorScheme("dark")}
              >
                Dark
              </Menu.Item>
              <Menu.Item
                rightSection={colorScheme === "auto" && <Icon name="check" />}
                onClick={() => changeColorScheme("auto")}
              >
                Auto (system)
              </Menu.Item>
            </Menu.Sub.Dropdown>
          </Menu.Sub>

          <Menu.Item
            onClick={() => {
              posthog.capture(posthogEvents.app_menu.settings_menu_item_click);
              settingsModalControl.open();
            }}
            leftSection={<MenuIcon name="cog" />}
          >
            Settings
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            td="none"
            leftSection={<MenuIcon name="circle-question" />}
            component="a"
            href="https://help.atinary.com/"
            target="_new"
            rightSection={<Icon name="arrow-up-right-from-square" color="dimmed" size="xs" />}
          >
            Help & Guides
          </Menu.Item>
          <Menu.Item
            td="none"
            leftSection={
              <img
                src="https://atinary.com/wp-content/uploads/2022/07/Atinary_logo-1.png"
                alt="Atinary logo"
                title="Atinary Technologies"
                width={20}
                height={20}
              />
            }
            component="a"
            href="https://atinary.com/"
            target="_new"
            rightSection={<Icon name="arrow-up-right-from-square" color="dimmed" size="xs" />}
            onClick={() => posthog.capture(posthogEvents.app_menu.atinary_menu_item_click)}
          >
            Atinary Technologies
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <Modal opened={settingsModalOpened} onClose={settingsModalControl.close} withCloseButton={false} size="auto">
        <ModalHeader title="Settings" onClose={settingsModalControl.close} />
        <ModalContent>
          <Settings />
        </ModalContent>
      </Modal>

      <VisuallyHidden>
        <BaseDatasetDropzone p={0} openRef={dropzoneRef} />
      </VisuallyHidden>
    </>
  );
}
